/**
 * HTML Page Tool Settings Panel
 *
 * Configure the runtime knobs for the `present_html_page` tool:
 * - prompt_addendum: appended to the tool's built-in docstring (LLM-visible)
 * - csp: embedded in the rendered page's <meta> CSP tag
 * - base_css: injected as a <style> reset block
 * - max_html_bytes: cap on the wrapped HTML byte size
 */

import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Textarea } from "@/components/ui/Textarea";
import { Loading } from "@/components/common";
import { AlertCircle, AlertTriangle, RotateCcw, Save } from "lucide-react";
import {
  useHtmlPageSystemConfig,
  useUpdateHtmlPageSystemConfig,
} from "../hooks/useSystemConfig";
import { systemApi } from "@/lib/api/system";
import type { HtmlPageSystemConfig } from "@/types/system";
import { useI18n } from "@/i18n";

const FALLBACK = {
  pageTitle: "HTML Page Tool Settings",
  pageDescription:
    "Tune the present_html_page tool: LLM-facing addendum, CSP, base CSS, and max HTML bytes. Changes take effect on the next tool call.",
  warningBanner:
    "These settings affect every agent that calls present_html_page.",
  promptAddendumLabel: "Prompt Addendum",
  promptAddendumHint: "Appended to the tool docstring (max 8KB).",
  cspLabel: "CSP Policy",
  cspHint: "Embedded in the <meta> CSP tag (max 2KB).",
  baseCssLabel: "Base CSS Reset",
  baseCssHint: "Injected inside <style> (max 4KB; no <style> tags).",
  maxBytesLabel: "Max HTML Bytes",
  maxBytesHint: "Range 10240–1048576. Default 200KB.",
  resetField: "Reset to default",
  save: "Save",
  saving: "Saving...",
  saveSuccess: "Saved",
  saveFailed: "Save failed",
  reload: "Reload",
  versionLabel: "Version",
  updatedByLabel: "Updated by",
  addendumTooLong: "Prompt addendum exceeds 8KB",
  cspRequired: "CSP cannot be empty",
  cssRequired: "Base CSS cannot be empty",
  bytesOutOfRange: "Max bytes must be between 10240 and 1048576",
};

const MIN_BYTES = 10240;
const MAX_BYTES = 1024 * 1024;
const ADDENDUM_BYTE_LIMIT = 8 * 1024;
const CSP_BYTE_LIMIT = 2 * 1024;
const BASE_CSS_BYTE_LIMIT = 4 * 1024;

const utf8Bytes = (s: string): number => new TextEncoder().encode(s).length;

export const HtmlPageSettingsTab: React.FC = () => {
  const { messages } = useI18n();
  const t = messages.systemSettings.htmlPage ?? FALLBACK;

  const { data: config, isLoading, error, refetch } = useHtmlPageSystemConfig();
  const updateConfig = useUpdateHtmlPageSystemConfig();

  const [promptAddendum, setPromptAddendum] = useState("");
  const [csp, setCsp] = useState("");
  const [baseCss, setBaseCss] = useState("");
  const [maxHtmlBytes, setMaxHtmlBytes] = useState<number>(204800);

  useEffect(() => {
    if (config) {
      setPromptAddendum(config.prompt_addendum ?? "");
      setCsp(config.csp ?? "");
      setBaseCss(config.base_css ?? "");
      setMaxHtmlBytes(config.max_html_bytes ?? 204800);
    }
  }, [config]);

  const handleResetField = async (field: keyof HtmlPageSystemConfig) => {
    try {
      const defaults = await systemApi.htmlPage.getDefaults();
      if (field === "prompt_addendum") setPromptAddendum(defaults.prompt_addendum);
      if (field === "csp") setCsp(defaults.csp);
      if (field === "base_css") setBaseCss(defaults.base_css);
      if (field === "max_html_bytes") setMaxHtmlBytes(defaults.max_html_bytes);
    } catch (err) {
      alert(`Failed to load defaults: ${(err as Error).message}`);
    }
  };

  const handleSave = async () => {
    if (utf8Bytes(promptAddendum) > ADDENDUM_BYTE_LIMIT) {
      alert(t.addendumTooLong);
      return;
    }
    if (!csp.trim()) {
      alert(t.cspRequired);
      return;
    }
    if (utf8Bytes(csp) > CSP_BYTE_LIMIT) {
      alert(`${t.cspLabel}: > ${CSP_BYTE_LIMIT}B`);
      return;
    }
    if (!baseCss.trim()) {
      alert(t.cssRequired);
      return;
    }
    if (utf8Bytes(baseCss) > BASE_CSS_BYTE_LIMIT) {
      alert(`${t.baseCssLabel}: > ${BASE_CSS_BYTE_LIMIT}B`);
      return;
    }
    if (
      !Number.isInteger(maxHtmlBytes) ||
      maxHtmlBytes < MIN_BYTES ||
      maxHtmlBytes > MAX_BYTES
    ) {
      alert(t.bytesOutOfRange);
      return;
    }

    try {
      const payload: HtmlPageSystemConfig = {
        prompt_addendum: promptAddendum,
        csp,
        base_css: baseCss,
        max_html_bytes: maxHtmlBytes,
      };
      await updateConfig.mutateAsync(payload);
      alert(t.saveSuccess);
    } catch (err) {
      alert(`${t.saveFailed}: ${(err as Error).message}`);
    }
  };

  if (isLoading) {
    return <Loading message="Loading..." />;
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
        <AlertCircle className="w-5 h-5" />
        <span>Load failed: {error.message}</span>
      </div>
    );
  }

  const hasChanges =
    promptAddendum !== (config?.prompt_addendum ?? "") ||
    csp !== (config?.csp ?? "") ||
    baseCss !== (config?.base_css ?? "") ||
    maxHtmlBytes !== (config?.max_html_bytes ?? 204800);

  return (
    <div className="space-y-6">
      {/* Warning banner */}
      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
        <div className="flex">
          <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0" />
          <div className="ml-3">
            <p className="text-sm text-yellow-700">{t.warningBanner}</p>
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">{t.pageTitle}</h2>
          <p className="text-sm text-gray-500 mt-1">{t.pageDescription}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            onClick={() => refetch()}
            disabled={updateConfig.isPending}
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            {t.reload}
          </Button>
          <Button
            onClick={handleSave}
            disabled={!hasChanges || updateConfig.isPending}
          >
            <Save className="w-4 h-4 mr-2" />
            {updateConfig.isPending ? t.saving : t.save}
          </Button>
        </div>
      </div>

      {/* Prompt addendum */}
      <Card className="p-6">
        <div className="flex justify-between items-center mb-2">
          <label className="text-sm font-medium text-gray-700">
            {t.promptAddendumLabel}{" "}
            <span className="text-xs text-gray-400 ml-2">
              {utf8Bytes(promptAddendum)} / {ADDENDUM_BYTE_LIMIT} B
            </span>
          </label>
          <button
            type="button"
            onClick={() => handleResetField("prompt_addendum")}
            className="text-xs text-blue-600 hover:text-blue-700"
          >
            <RotateCcw className="w-3 h-3 inline mr-1" />
            {t.resetField}
          </button>
        </div>
        <Textarea
          value={promptAddendum}
          onChange={(e) => setPromptAddendum(e.target.value)}
          rows={6}
          className="font-mono text-sm"
        />
        <p className="mt-2 text-xs text-gray-500">{t.promptAddendumHint}</p>
      </Card>

      {/* CSP */}
      <Card className="p-6">
        <div className="flex justify-between items-center mb-2">
          <label className="text-sm font-medium text-gray-700">
            {t.cspLabel}{" "}
            <span className="text-xs text-gray-400 ml-2">
              {utf8Bytes(csp)} / {CSP_BYTE_LIMIT} B
            </span>
          </label>
          <button
            type="button"
            onClick={() => handleResetField("csp")}
            className="text-xs text-blue-600 hover:text-blue-700"
          >
            <RotateCcw className="w-3 h-3 inline mr-1" />
            {t.resetField}
          </button>
        </div>
        <Textarea
          value={csp}
          onChange={(e) => setCsp(e.target.value)}
          rows={4}
          className="font-mono text-sm"
        />
        <p className="mt-2 text-xs text-gray-500">{t.cspHint}</p>
      </Card>

      {/* Base CSS */}
      <Card className="p-6">
        <div className="flex justify-between items-center mb-2">
          <label className="text-sm font-medium text-gray-700">
            {t.baseCssLabel}{" "}
            <span className="text-xs text-gray-400 ml-2">
              {utf8Bytes(baseCss)} / {BASE_CSS_BYTE_LIMIT} B
            </span>
          </label>
          <button
            type="button"
            onClick={() => handleResetField("base_css")}
            className="text-xs text-blue-600 hover:text-blue-700"
          >
            <RotateCcw className="w-3 h-3 inline mr-1" />
            {t.resetField}
          </button>
        </div>
        <Textarea
          value={baseCss}
          onChange={(e) => setBaseCss(e.target.value)}
          rows={12}
          className="font-mono text-sm"
        />
        <p className="mt-2 text-xs text-gray-500">{t.baseCssHint}</p>
      </Card>

      {/* Max HTML bytes */}
      <Card className="p-6">
        <div className="flex justify-between items-center mb-2">
          <label className="text-sm font-medium text-gray-700">
            {t.maxBytesLabel}
          </label>
          <button
            type="button"
            onClick={() => handleResetField("max_html_bytes")}
            className="text-xs text-blue-600 hover:text-blue-700"
          >
            <RotateCcw className="w-3 h-3 inline mr-1" />
            {t.resetField}
          </button>
        </div>
        <input
          type="number"
          min={MIN_BYTES}
          max={MAX_BYTES}
          step={1024}
          value={maxHtmlBytes}
          onChange={(e) => setMaxHtmlBytes(Number(e.target.value))}
          className="w-full px-3 py-2 border border-gray-300 rounded-md font-mono text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
        <p className="mt-2 text-xs text-gray-500">{t.maxBytesHint}</p>
      </Card>

      {/* Metadata */}
      {config?.version !== undefined && (
        <div className="text-sm text-gray-500">
          <p>
            {t.versionLabel}: <span>{config.version}</span> | {t.updatedByLabel}:{" "}
            <span>{config.updated_by || "N/A"}</span> |{" "}
            <span>
              {config.updated_at
                ? new Date(config.updated_at).toLocaleString()
                : "N/A"}
            </span>
          </p>
        </div>
      )}
    </div>
  );
};

export default HtmlPageSettingsTab;
